import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { QrCodeIcon, PackageIcon, LockKeyIcon, WarningCircleIcon, CopyIcon, ChartLineUpIcon } from "@phosphor-icons/react";
import api, { resolveUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const ST = { active: "border-primary/40 text-primary", pending_review: "border-accent/40 text-accent", unclaimed: "border-white/25 text-white/50" };

function PinCard({ tag, onChanged }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState(tag.destination_url || "");
  const [busy, setBusy] = useState(false);
  const [confirmMode, setConfirmMode] = useState(null); // "activate" | "edit"
  const active = tag.status === "active";
  const scanUrl = resolveUrl(tag.code);
  const dirty = url.trim() !== (tag.destination_url || "").trim();

  const saveDraft = async () => {
    if (!url.trim()) return toast.error("Enter a destination URL");
    setBusy(true);
    try { await api.put(`/portal/tags/${tag.code}`, { destination_url: url.trim() }); toast.success("Saved"); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  const activate = async () => {
    if (!url.trim()) return toast.error("Enter a destination URL first");
    setBusy(true);
    try {
      await api.put(`/portal/tags/${tag.code}`, { destination_url: url.trim() });
      await api.post(`/portal/tags/${tag.code}/activate`);
      toast.success("Pin is now live!");
      setConfirmMode(null); onChanged();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not activate"); } finally { setBusy(false); }
  };
  const saveLiveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/portal/tags/${tag.code}`, { destination_url: url.trim() });
      toast.success("Destination updated — live now");
      setConfirmMode(null); onChanged();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); } finally { setBusy(false); }
  };

  return (
    <div className="border border-border bg-card p-5" data-testid={`pin-${tag.code}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono text-sm text-primary">/{tag.code}</span>
        <Badge variant="outline" className={`font-mono text-[10px] uppercase ${ST[tag.status] || ""}`} data-testid={`pin-status-${tag.code}`}>{tag.status}</Badge>
      </div>

      {/* Permanent scan URL */}
      <div className="border border-border bg-secondary/20 flex items-center justify-between gap-2 px-3 py-2 mb-3">
        <div className="min-w-0">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">Your permanent link</p>
          <p data-testid={`pin-scanurl-${tag.code}`} className="font-mono text-xs text-white/80 truncate">{scanUrl}</p>
        </div>
        <button data-testid={`pin-copy-${tag.code}`} onClick={() => { navigator.clipboard.writeText(scanUrl); toast.success("Link copied"); }} className="text-white/40 hover:text-primary transition-colors shrink-0" title="Copy link">
          <CopyIcon weight="bold" />
        </button>
      </div>

      <Input
        data-testid={`pin-url-${tag.code}`}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://where-it-should-point.com"
        className="border-border font-mono text-sm mb-3 bg-secondary/40 focus-visible:ring-primary"
      />
      <div className="flex gap-2">
        {!active && (
          <>
            <Button data-testid={`pin-save-${tag.code}`} onClick={saveDraft} disabled={busy} variant="outline" className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">SAVE LINK</Button>
            <Button data-testid={`pin-activate-${tag.code}`} onClick={() => (url.trim() ? setConfirmMode("activate") : toast.error("Enter a destination URL first"))} disabled={busy} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">CONFIRM &amp; ACTIVATE</Button>
          </>
        )}
        {active && (
          <>
            <Button data-testid={`pin-save-${tag.code}`} onClick={() => (dirty && url.trim() ? setConfirmMode("edit") : toast.info("Change the link first"))} disabled={busy || !dirty} variant="outline" className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs disabled:opacity-40">SAVE CHANGES</Button>
            <a href={resolveUrl(tag.code)} target="_blank" rel="noreferrer" data-testid={`pin-live-${tag.code}`} className="inline-flex items-center gap-1.5 border border-primary/40 text-primary px-3 py-2 text-xs font-mono hover:bg-primary/10 transition-colors"><QrCodeIcon weight="bold" /> LIVE</a>
          </>
        )}
        <Button data-testid={`pin-analytics-${tag.code}`} onClick={() => navigate(`/pin/${tag.code}`)} variant="outline" className="ml-auto border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs"><ChartLineUpIcon weight="bold" className="mr-1.5" /> ANALYTICS</Button>
      </div>
      {!active && <p className="font-mono text-[10px] text-white/40 mt-2">Not live yet — confirm the link to activate your pin.</p>}
      {active && (
        <p className="font-mono text-[10px] text-white/40 mt-2 flex items-center gap-1.5" data-testid={`pin-live-note-${tag.code}`}>
          <QrCodeIcon weight="fill" className="text-primary" /> Live — you can update the destination any time (we’ll ask you to confirm).
        </p>
      )}

      <AlertDialog open={!!confirmMode} onOpenChange={(o) => !o && setConfirmMode(null)}>
        <AlertDialogContent data-testid={`activate-confirm-${tag.code}`} className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              <WarningCircleIcon weight="fill" className="text-accent" />
              {confirmMode === "edit" ? "Update this live pin?" : "Activate this pin?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {confirmMode === "edit"
                ? <>This pin is <span className="text-white font-semibold">live</span> — changing it updates instantly for everyone who scans it. New destination:</>
                : <>Your pin will go live immediately. You can still edit it later (we’ll ask you to confirm each change). Destination:</>}
              <span className="block mt-3 font-mono text-xs text-primary break-all border border-border bg-secondary/30 p-2">{url}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`activate-cancel-${tag.code}`} className="border-border bg-transparent hover:bg-secondary font-mono text-xs">GO BACK</AlertDialogCancel>
            <AlertDialogAction data-testid={`activate-confirm-btn-${tag.code}`} onClick={(e) => { e.preventDefault(); confirmMode === "edit" ? saveLiveEdit() : activate(); }} disabled={busy} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90">
              {busy ? "SAVING…" : confirmMode === "edit" ? "UPDATE LIVE PIN" : "ACTIVATE"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function CustomerPortal() {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, data: null });
  const [tags, setTags] = useState([]);

  const loadTags = () => { api.get("/portal/tags").then(({ data }) => setTags(data)).catch(() => setTags([])); };

  useEffect(() => {
    if (user?.is_admin) return;
    api.get("/portal/orders").then(({ data }) => setState({ loading: false, data })).catch(() => setState({ loading: false, data: null }));
    loadTags();
  }, [user]);

  if (user?.is_admin) return <Navigate to="/admin" replace />;

  const orders = state.data?.orders || [];

  return (
    <Layout>
      <div className="mb-10">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary mb-3">// Your account</p>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">Your D Codes</h1>
        <p className="text-white/50 mt-4">Confirm where each pin points to activate it. Nothing goes live until you approve — a typo costs nothing.</p>
      </div>

      {tags.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4 mb-12" data-testid="portal-pins">
          {tags.map((t) => <PinCard key={t.code} tag={t} onChanged={loadTags} />)}
        </div>
      )}

      {state.loading ? (
        <div className="border border-border"><div className="px-6 py-6 border-b border-border"><Skeleton className="h-8 w-full bg-secondary/40" /></div></div>
      ) : orders.length === 0 && tags.length === 0 ? (
        <div data-testid="portal-empty" className="border border-border flex flex-col items-center text-center py-20 px-6">
          <PackageIcon size={44} weight="duotone" className="text-white/30" />
          <h3 className="font-heading font-black text-2xl tracking-tight mt-6">No D Codes yet</h3>
          <p className="text-white/50 mt-3 max-w-sm">Your D Code products will appear here after purchase. Sign in with the email you used at checkout.</p>
        </div>
      ) : null}
    </Layout>
  );
}

